import type {
  Checklist,
  ChecklistItem,
  IAPMAppDataService,
  ICDFView,
  InFieldUser,
  SearchOperator,
} from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type {
  CogniteClientInstancesFDMDependencies,
  CogniteClientViewsFDMDependencies,
  Filters,
  Sort,
} from '@cognite/fdm-client';
import type {
  CogniteClient,
  DirectRelationReference,
  SearchSort,
} from '@cognite/sdk';

import {
  getSourceCreatedFields,
  getSourceUpdatedFields,
} from '../../features/shared-utils/sourceable-utils';
import { TEMPLATE_TASK_LIMIT } from '../../features/template/constants';
import { getCdmViewReferencesFromModelVersion } from '../../providers/fdm-services/cdm-view-reference-utils';
import { unwrapDmsSearchResult } from '../../utils/unwrap-dms-search-result';

import {
  mapChecklistItemToUpsertType,
  mapChecklistToUpsertType,
} from './cdm-checklist-service-utils';
import {
  mapChecklistResponsesToChecklists,
  normalizeChecklistFromDms,
} from './utils/map-checklist-response-to-checklist';
import type {
  CDMChecklistItemResponseType,
  CDMChecklistResponseType,
} from './cdm-types';
import type { IChecklistService } from './checklist-service-interface';
import { valueAsInstanceId } from './hooks/utils/type-utils';
import type {
  ChecklistAggregate,
  ChecklistItemStatusAggregationResponse,
} from './types';
import { buildChecklistSearchSort } from './utils/build-checklist-search-sort';
import { buildChecklistDetailPropertyTree } from './utils/build-checklist-items-property-tree';
import { mapChecklistItemAggregateToStatusAggregation } from './utils/map-checklist-item-aggregate-to-status-aggregation';

export type ChecklistsDmsSearchFn = (
  query?: string,
  instanceType?: 'node',
  filter?: Filters,
  limit?: number,
  properties?: string[],
  showArchived?: boolean,
  sort?: SearchSort[],
  searchOperator?: SearchOperator,
  cursor?: string
) => Promise<
  | CDMChecklistResponseType[]
  | { items: CDMChecklistResponseType[]; nextCursor?: string }
>;

export type APMClientDependencies = {
  checklists: Pick<
    IAPMAppDataService<CDMChecklistResponseType>,
    'list' | 'upsert' | 'delete'
  > & {
    dmsSearch: ChecklistsDmsSearchFn;
  };
  checklistItems: Pick<
    IAPMAppDataService<CDMChecklistItemResponseType>,
    'upsert' | 'delete'
  >;
  checklistsDMS: ICDFView<Checklist>;
  appDataModelSpace: string;
  appDataModelName: string;
  appDataModelVersion: string;
  appDataInstanceSpace: string;
  userInstanceSpace: string;
  supportsSolutionTags: () => boolean;
};

export type CogniteClientDependencies = Pick<
  CogniteClient,
  'post' | 'getBaseUrl' | 'project'
> & {
  instances: CogniteClientInstancesFDMDependencies;
  views: CogniteClientViewsFDMDependencies;
};

export class CdmChecklistService
  extends FDMClient
  implements IChecklistService
{
  apmClient: APMClientDependencies;
  checklistItemsDMS: ICDFView<ChecklistItem>;
  modelSpace: string;
  modelName: string;
  modelVersion: string;
  instanceSpace: string;
  rootLocation?: DirectRelationReference;
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies,
    checklistItemsDMS: ICDFView<ChecklistItem>,
    rootLocation?: DirectRelationReference
  ) {
    super(client);
    this.apmClient = apmClient;
    this.checklistItemsDMS = checklistItemsDMS;
    this.modelSpace = apmClient.appDataModelSpace;
    this.modelName = apmClient.appDataModelName;
    this.modelVersion = apmClient.appDataModelVersion;
    this.instanceSpace = apmClient.appDataInstanceSpace;
    this.rootLocation = rootLocation;
  }

  async getChecklists(
    filters?: Filters,
    sort?: Sort,
    nextCursor?: string,
    /*
    Status chips still aggregate checklist items in chunks of 20. Keep the
    search page size aligned so `paginatedChecklistIds` stays one array per
    aggregate request.
    */
    pageSize = 20,
    query?: string,
    properties?: string[]
  ): Promise<{
    items: Checklist[];
    pageInfo: { endCursor: string; hasNextPage: boolean };
  }> {
    const { items, nextCursor: searchCursor } = await this.searchChecklists(
      filters,
      sort,
      pageSize,
      query,
      nextCursor,
      properties
    );

    return {
      items,
      pageInfo: {
        endCursor: searchCursor ?? '',
        hasNextPage: Boolean(searchCursor),
      },
    };
  }

  async searchChecklists(
    filters?: Filters,
    sort?: Sort,
    limit?: number,
    query?: string,
    cursor?: string,
    properties?: string[]
  ): Promise<{ items: Checklist[]; nextCursor?: string }> {
    const checklistView = getCdmViewReferencesFromModelVersion(
      this.modelVersion
    ).infieldChecklist;

    const searchSort = buildChecklistSearchSort({
      sort,
      checklistView,
    });

    const { items, nextCursor } = unwrapDmsSearchResult(
      await this.apmClient.checklists.dmsSearch(
        query,
        'node',
        filters,
        limit,
        properties,
        false,
        searchSort,
        'AND',
        cursor
      )
    );

    return {
      items: mapChecklistResponsesToChecklists(items),
      nextCursor,
    };
  }

  async getChecklistsAndChecklistItems(
    filters?: Filters,
    sort?: Sort,
    nextCursor?: string,
    pageSize?: number
  ): Promise<{
    items: Checklist[];
    pageInfo: { endCursor: string; hasNextPage: boolean };
  }> {
    const resolvedPageSize = pageSize ?? TEMPLATE_TASK_LIMIT;

    const result = await this.apmClient.checklistsDMS.list({
      filters,
      sort,
      nextCursor,
      pageSize: resolvedPageSize,
      autoPaginateRelations: true,
      propertyTree: {
        checklistItems: {
          limit: TEMPLATE_TASK_LIMIT,
          propertyTree: {
            asset: {
              limit: 1,
            },
          },
        },
      },
    });

    return {
      items: result.items,
      pageInfo: {
        endCursor: result.pageInfo.nextCursor ?? '',
        hasNextPage: result.pageInfo.hasNextPage,
      },
    };
  }

  async getChecklistData(
    checklistFilter?: Filters,
    checklistSort?: Sort,
    itemsFilter?: Filters,
    _itemsSort?: Sort,
    isObservationsEnabled?: boolean,
    pageSize = 1000
  ): Promise<Checklist> {
    const itemsLimit = Math.min(pageSize, TEMPLATE_TASK_LIMIT);

    const filters: Filters = {
      and: [
        ...(checklistFilter ? [checklistFilter] : []),
        {
          equals: {
            property: 'space',
            eq: this.instanceSpace,
          },
        },
      ],
    };

    const result = await this.apmClient.checklistsDMS.list({
      filters,
      sort: checklistSort,
      pageSize: 1,
      autoPaginateRelations: true,
      propertyTree: buildChecklistDetailPropertyTree({
        itemsFilter,
        itemsLimit,
        includeObservations: isObservationsEnabled ?? false,
      }),
    });

    const checklist = result.items[0];

    if (checklist === undefined) {
      throw new Error('Checklist not found');
    }

    return normalizeChecklistFromDms(checklist);
  }

  async aggregate(
    field: string,
    isDirectRelation: boolean,
    filters?: Filters
  ): Promise<ChecklistAggregate[]> {
    const res = await this.apmClient.checklistsDMS.aggregate({
      filters,
      aggregates: [{ count: { property: field } }],
      groupBy: [field],
      searchOperator: 'AND',
    });

    return res.items.map((item) => {
      const itemField = item.group?.[field];
      const aggregateField = item.aggregates.find(
        (x) => x.aggregate === 'count'
      );
      const isAggregatedNumberValue =
        aggregateField && 'value' in aggregateField;

      return {
        value: isDirectRelation
          ? valueAsInstanceId(itemField) // Return value from Cognite SDK is not accounting for direct relationships
          : itemField,
        count: isAggregatedNumberValue ? aggregateField.value : undefined,
      };
    });
  }

  async createChecklist(checklist: Checklist, user: InFieldUser) {
    const upsertedChecklist: Checklist = {
      ...checklist,
      ...getSourceCreatedFields(user),
    };

    return this.upsertChecklist([upsertedChecklist]);
  }

  async updateChecklists(checklists: Checklist[], user: InFieldUser) {
    const upsertedChecklists: Checklist[] = checklists.map((checklist) => ({
      ...checklist,
      ...getSourceUpdatedFields(user),
    }));

    return this.upsertChecklist(upsertedChecklists);
  }

  async upsertChecklist(checklists: Checklist[]) {
    if (!this.rootLocation) {
      throw new Error('Missing root location for checklist service');
    }
    const rootLocation = this.rootLocation;
    const upsertedChecklists = checklists.map((checklist) =>
      mapChecklistToUpsertType(checklist, rootLocation, this.instanceSpace)
    );
    await this.apmClient.checklists.upsert(upsertedChecklists);

    return checklists;
  }

  async getChecklistItemObservations(
    filters?: Filters,
    sort?: Sort,
    pageSize = TEMPLATE_TASK_LIMIT
  ): Promise<ChecklistItem[]> {
    const result = await this.checklistItemsDMS.list({
      filters,
      sort,
      pageSize,
      autoPaginateRelations: true,
      propertyTree: {
        asset: {
          limit: 1,
        },
        observations: {
          limit: TEMPLATE_TASK_LIMIT,
        },
      },
    });

    return result.items;
  }

  async aggregateChecklistItemStatuses(
    checklistExternalIds: string[]
  ): Promise<ChecklistItemStatusAggregationResponse> {
    if (checklistExternalIds.length === 0) {
      return {};
    }

    const filters: Filters = {
      and: [
        {
          in: {
            property: 'checklist',
            in: checklistExternalIds.map((externalId: string) => ({
              externalId,
              space: this.instanceSpace,
            })),
          },
        },
        {
          equals: {
            property: 'space',
            eq: this.instanceSpace,
          },
        },
      ],
    };

    const aggregateParams = {
      filters,
      searchOperator: 'AND' as const,
      limit: 1000,
      aggregates: [{ count: { property: 'externalId' } }],
    };

    const [totalResponse, statusResponse] = await Promise.all([
      this.checklistItemsDMS.aggregate({
        ...aggregateParams,
        groupBy: ['checklist'],
      }),
      this.checklistItemsDMS.aggregate({
        ...aggregateParams,
        groupBy: ['checklist', 'status'],
      }),
    ]);

    return mapChecklistItemAggregateToStatusAggregation({
      checklistExternalIds,
      totalGroupItems: totalResponse.items,
      statusGroupItems: statusResponse.items,
    });
  }

  async getChecklistItemsStatus(
    checklistFilter?: Filters,
    checklistSort?: Sort,
    itemsFilter?: Filters,
    _itemsSort?: Sort,
    pageSize = 1000
  ): Promise<Checklist[]> {
    const filters: Filters = {
      and: [
        ...(checklistFilter ? [checklistFilter] : []),
        {
          equals: {
            property: 'space',
            eq: this.instanceSpace,
          },
        },
      ],
    };

    const result = await this.apmClient.checklistsDMS.list({
      filters,
      sort: checklistSort,
      pageSize,
      autoPaginateRelations: true,
      propertyTree: {
        checklistItems: {
          limit: TEMPLATE_TASK_LIMIT,
          filters: itemsFilter,
        },
      },
    });

    return result.items;
  }

  async createChecklistItems(
    checklistItems: ChecklistItem[],
    user: InFieldUser
  ) {
    const createdChecklistItems: ChecklistItem[] = checklistItems.map(
      (checklistItem) => ({
        ...checklistItem,
        ...getSourceCreatedFields(user),
      })
    );

    return this.upsertChecklistItems(createdChecklistItems);
  }

  async updateChecklistItems(
    checklistItems: ChecklistItem[],
    user: InFieldUser
  ) {
    const updatedChecklistItems: ChecklistItem[] = checklistItems.map(
      (checklistItem) => ({
        ...checklistItem,
        ...getSourceUpdatedFields(user),
      })
    );

    return this.upsertChecklistItems(updatedChecklistItems);
  }

  async upsertChecklistItems(checklistItems: ChecklistItem[]) {
    const filesSpace = this.apmClient.appDataInstanceSpace;
    const upsertedChecklistItem = checklistItems.map((checklistItem) =>
      mapChecklistItemToUpsertType(checklistItem, filesSpace)
    );

    const upsertedChecklistItems = await this.apmClient.checklistItems.upsert(
      upsertedChecklistItem
    );

    const mappedItems = upsertedChecklistItems.data.items.map(
      (checklistItem) => ({
        ...checklistItem,
        files: checklistItem.files?.map((file) => file.externalId),
      })
    );

    return {
      ...upsertedChecklistItems,
      data: {
        items: mappedItems,
      },
    };
  }

  async softDeleteChecklists(externalIds: string[]) {
    const checklists: CDMChecklistResponseType[] = externalIds.map(
      (externalId) => ({
        externalId,
        isArchived: true,
      })
    );
    await this.apmClient.checklists.upsert(checklists);
    return externalIds;
  }

  async deleteChecklist(externalIds: string[]) {
    return this.apmClient.checklists.delete(externalIds);
  }

  async deleteChecklistItems(externalIds: string[]) {
    return this.apmClient.checklistItems.delete(externalIds);
  }
}

export const createCdmChecklistService = (
  ...args: ConstructorParameters<typeof CdmChecklistService>
): IChecklistService => new CdmChecklistService(...args);
